(() => {
  const SELECTOR = '[data-zl-compatibility]';
  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 300;

  const normalizeInput = (value) =>
    value
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };

  function initialize(root) {
    if (!root || root.dataset.compatibilityReady === 'true') return;
    root.dataset.compatibilityReady = 'true';

    const signal = root._zlCompatibilitySignal;
    const tabs = [...root.querySelectorAll('[data-category-tab]')];
    const productCards = [...root.querySelectorAll('[data-product-card]')];
    const categoryEmpty = root.querySelector('[data-category-empty]');
    const productGrid = root.querySelector('[data-product-grid]');
    const productsMore = root.querySelector('[data-products-more]');
    const productsMoreLabel = root.querySelector('[data-products-more-label]');
    const modal = root.querySelector('[data-compatibility-modal]');
    const modalDialog = root.querySelector('[data-modal-dialog]');
    const modalProductName = root.querySelector('[data-modal-product-name]');
    const modalCloseButtons = [...root.querySelectorAll('[data-modal-close]')];
    const searchView = root.querySelector('[data-search-view]');
    const form = root.querySelector('[data-compatibility-form]');
    const phoneField = root.querySelector('.zl-phone-field');
    const phoneInput = root.querySelector('[data-phone-input]');
    const phoneClear = root.querySelector('[data-phone-clear]');
    const phoneOptions = root.querySelector('[data-phone-options]');
    const resultsWrap = root.querySelector('[data-results-wrap]');
    const resultsCount = root.querySelector('[data-results-count]');
    const selectedPhoneBox = root.querySelector('[data-selected-phone]');
    const selectedPhoneName = root.querySelector('[data-selected-phone-name]');
    const selectedPhoneBrand = root.querySelector('[data-selected-phone-brand]');
    const changePhone = root.querySelector('[data-change-phone]');
    const phoneHelp = root.querySelector('[data-phone-help]');
    const submit = root.querySelector('[data-submit]');
    const result = root.querySelector('[data-result]');
    const resultIcon = root.querySelector('[data-result-icon]');
    const resultKicker = root.querySelector('[data-result-kicker]');
    const resultTitle = root.querySelector('[data-result-title]');
    const resultMessage = root.querySelector('[data-result-message]');
    const resultPhone = root.querySelector('[data-result-phone]');
    const resultProduct = root.querySelector('[data-result-product]');
    const resultStatus = root.querySelector('[data-result-status]');
    const resultOsRow = root.querySelector('[data-result-os-row]');
    const resultOs = root.querySelector('[data-result-os]');
    const resultContact = root.querySelector('[data-result-contact]');
    const resultProductLink = root.querySelector('[data-result-product-link]');
    const checkAnother = root.querySelector('[data-check-another]');

    if (
      !modal || !modalDialog || !modalProductName || !searchView || !form || !phoneField ||
      !phoneInput || !phoneClear || !phoneOptions || !resultsWrap || !resultsCount ||
      !selectedPhoneBox || !selectedPhoneName || !selectedPhoneBrand || !changePhone ||
      !phoneHelp || !submit || !result || !resultIcon || !resultKicker || !resultTitle ||
      !resultMessage || !resultPhone || !resultProduct || !resultStatus || !resultOsRow ||
      !resultOs || !resultContact || !resultProductLink || !checkAnother
    ) return;

    const initialHelp = phoneHelp.textContent.trim();
    const apiBase = (root.dataset.apiBase || '').replace(/\/$/, '');
    let selectedPhone = null;
    let selectedProduct = null;
    let lastTrigger = null;
    let debounceTimer = null;
    let searchController = null;
    let compatibilityController = null;
    let activeOptionIndex = -1;
    let optionButtons = [];
    let activeCategory = tabs.find((tab) => tab.classList.contains('is-active'))?.dataset.categoryTab || 'phone-thermal-camera';
    let productsExpanded = false;
    let resizeTimer = null;


    const getProductLimit = () => {
      if (window.innerWidth <= 767) return 6;
      if (!productGrid) return 1;
      const columns = window.getComputedStyle(productGrid).gridTemplateColumns.split(' ').filter(Boolean).length;
      return Math.max(columns, 1);
    };

    const updateProductsMore = (visibleCount) => {
      if (!productsMore || !productsMoreLabel) return;
      const limit = getProductLimit();
      const shouldShowToggle = visibleCount > limit;
      productsMore.hidden = !shouldShowToggle;
      productsMore.setAttribute('aria-expanded', shouldShowToggle && productsExpanded ? 'true' : 'false');
      productsMoreLabel.textContent = shouldShowToggle && productsExpanded
        ? (productsMore.dataset.lessLabel || 'Show Less')
        : (productsMore.dataset.moreLabel || 'Show More');
    };

    const applyProductVisibility = () => {
      const limit = getProductLimit();
      let visibleCount = 0;

      productCards.forEach((card) => {
        const matchesCategory = card.dataset.productType === activeCategory;
        card.classList.toggle('is-filtered', !matchesCategory);

        if (!matchesCategory) {
          card.classList.remove('is-hidden-by-limit');
          card.setAttribute('aria-hidden', 'true');
          return;
        }

        const hiddenByLimit = !productsExpanded && visibleCount >= limit;
        card.classList.toggle('is-hidden-by-limit', hiddenByLimit);
        card.setAttribute('aria-hidden', hiddenByLimit ? 'true' : 'false');
        visibleCount += 1;
      });

      if (categoryEmpty) categoryEmpty.hidden = visibleCount !== 0;
      updateProductsMore(visibleCount);
    };

    const updateSubmit = () => {
      submit.disabled = !apiBase || !selectedPhone || !selectedProduct || submit.classList.contains('is-loading');
    };

    const updateClearButton = () => {
      phoneClear.hidden = phoneInput.value.length === 0 || phoneField.classList.contains('is-searching');
    };

    const hideResults = () => {
      resultsWrap.hidden = true;
      phoneInput.setAttribute('aria-expanded', 'false');
      phoneInput.removeAttribute('aria-activedescendant');
      activeOptionIndex = -1;
    };

    const showSearchView = () => {
      searchView.hidden = false;
      result.hidden = true;
    };

    const setHelp = (message, withContact = false) => {
      phoneHelp.replaceChildren(document.createTextNode(message));
      if (withContact) {
        phoneHelp.append(document.createTextNode(' '));
        const link = createElement('a', '', 'Contact us');
        link.href = root.dataset.contactUrl || '/pages/contact-us';
        phoneHelp.append(link);
      }
    };

    const clearSelectedPhone = ({ keepInput = true } = {}) => {
      selectedPhone = null;
      selectedPhoneBox.hidden = true;
      if (!keepInput) phoneInput.value = '';
      updateClearButton();
      updateSubmit();
    };

    const resetChecker = () => {
      window.clearTimeout(debounceTimer);
      window.clearTimeout(resizeTimer);
      searchController?.abort();
      compatibilityController?.abort();
      phoneField.classList.remove('is-searching');
      submit.classList.remove('is-loading');
      phoneInput.value = '';
      selectedPhone = null;
      optionButtons = [];
      phoneOptions.replaceChildren();
      selectedPhoneBox.hidden = true;
      resultsWrap.hidden = true;
      resultsCount.textContent = '';
      phoneInput.setAttribute('aria-expanded', 'false');
      phoneInput.removeAttribute('aria-activedescendant');
      activeOptionIndex = -1;
      result.hidden = true;
      searchView.hidden = false;
      resultProductLink.hidden = true;
      setHelp(apiBase ? initialHelp : 'Add the Compatibility API base URL in this section’s theme settings.');
      updateClearButton();
      updateSubmit();
    };

    const showResult = ({
      status,
      icon,
      kicker,
      title,
      message,
      phone,
      product,
      statusLabel,
      os,
      contact,
    }) => {
      result.dataset.status = status;
      resultIcon.textContent = icon;
      resultKicker.textContent = kicker;
      resultTitle.textContent = title;
      resultMessage.textContent = message;
      resultPhone.textContent = phone || '';
      resultProduct.textContent = product || '';
      resultStatus.textContent = statusLabel || '';
      resultOs.textContent = os || '';
      resultOsRow.hidden = !os;
      resultContact.hidden = !contact;
      resultProductLink.hidden = !selectedProduct?.url;
      if (selectedProduct?.url) resultProductLink.href = selectedProduct.url;

      searchView.hidden = true;
      result.hidden = false;
      modalDialog.scrollTop = 0;
      result.focus({ preventScroll: true });
    };

    const openModal = (card) => {
      selectedProduct = {
        key: card.dataset.productKey,
        name: card.dataset.productName || card.dataset.productKey,
        url: card.dataset.productUrl || '',
      };
      lastTrigger = card;
      modalProductName.textContent = selectedProduct.name;
      resetChecker();
      modal.hidden = false;
      document.body.classList.add('zl-compatibility-modal-open');
      window.requestAnimationFrame(() => phoneInput.focus());
    };

    const closeModal = () => {
      if (modal.hidden) return;
      resetChecker();
      modal.hidden = true;
      selectedProduct = null;
      if (!document.querySelector('.zl-compatibility-modal:not([hidden])')) {
        document.body.classList.remove('zl-compatibility-modal-open');
      }
      lastTrigger?.focus({ preventScroll: true });
      lastTrigger = null;
    };

    const copyText = async (text) => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (error) {
        // Fall back to execCommand for browsers that block the Clipboard API.
      }

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.append(textarea);
      textarea.select();

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      }
      textarea.remove();
      return copied;
    };

    const openShopifyInbox = async () => {
      let chat = document.querySelector('shopify-chat');

      if (!chat && window.customElements) {
        await Promise.race([
          window.customElements.whenDefined('shopify-chat').catch(() => undefined),
          new Promise((resolve) => window.setTimeout(resolve, 1800)),
        ]);
        chat = document.querySelector('shopify-chat');
      }

      if (!chat) return false;
      chat.setAttribute('open', '');
      return true;
    };

    const buildSupportMessage = () => {
      const status = resultStatus.textContent.trim() || resultTitle.textContent.trim();
      const os = resultOs.textContent.trim();

      return [
        'Hi, I need help with a Thermal Master compatibility check.',
        selectedPhone?.model ? `Phone: ${selectedPhone.model}` : '',
        selectedProduct?.name ? `Product: ${selectedProduct.name}` : '',
        status ? `Status: ${status}` : '',
        os ? `Tested OS: ${os}` : '',
        `Page: ${window.location.href}`,
      ].filter(Boolean).join('\n');
    };

    const selectPhone = (phone) => {
      selectedPhone = phone;
      phoneInput.value = phone.model;
      selectedPhoneName.textContent = phone.model;
      selectedPhoneBrand.textContent = phone.brand || '';
      selectedPhoneBox.hidden = false;
      hideResults();
      optionButtons = [];
      setHelp('Exact phone model selected.');
      updateClearButton();
      updateSubmit();
    };

    const renderOptions = (phones) => {
      phoneOptions.replaceChildren();
      resultsCount.textContent = phones.length ? `${phones.length} match${phones.length === 1 ? '' : 'es'}` : '';
      optionButtons = phones.map((phone, index) => {
        const button = createElement('button', 'zl-phone-option');
        button.type = 'button';
        button.id = `${phoneOptions.id}-option-${index}`;
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', 'false');

        const main = createElement('span', 'zl-phone-option__main');
        main.append(
          createElement('strong', 'zl-phone-option__model', phone.model),
          createElement('span', 'zl-phone-option__meta', 'Select exact model'),
        );
        button.append(
          main,
          createElement('span', 'zl-phone-option__brand', phone.brand || ''),
        );

        button.addEventListener('click', () => selectPhone(phone), { signal });
        phoneOptions.append(button);
        return button;
      });

      resultsWrap.hidden = phones.length === 0;
      phoneInput.setAttribute('aria-expanded', phones.length ? 'true' : 'false');
      activeOptionIndex = -1;
    };

    const activateOption = (nextIndex) => {
      if (!optionButtons.length) return;
      activeOptionIndex = (nextIndex + optionButtons.length) % optionButtons.length;
      optionButtons.forEach((button, index) => {
        const active = index === activeOptionIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      const activeButton = optionButtons[activeOptionIndex];
      phoneInput.setAttribute('aria-activedescendant', activeButton.id);
      activeButton.scrollIntoView({ block: 'nearest' });
    };

    const searchPhones = async (query) => {
      searchController?.abort();
      const controller = new AbortController();
      searchController = controller;
      phoneField.classList.add('is-searching');
      updateClearButton();

      try {
        const response = await fetch(`${apiBase}/api/phones/search?${new URLSearchParams({ q: query })}`, {
          signal: controller.signal,
          credentials: 'omit',
          headers: { Accept: 'application/json' },
        });
        const payload = await response.json();

        if (!response.ok || payload.success !== true || !Array.isArray(payload.data)) {
          throw new Error(payload.message || 'Phone search failed.');
        }

        renderOptions(payload.data);
        if (payload.data.length === 0) {
          setHelp('Phone model not found in our compatibility database.', true);
        } else {
          setHelp('Select your exact phone model from the results.');
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          hideResults();
          optionButtons = [];
          setHelp('We could not search phone models right now. Please try again.', true);
        }
      } finally {
        if (searchController === controller) {
          phoneField.classList.remove('is-searching');
          updateClearButton();
        }
      }
    };

    const filterProducts = (category) => {
      activeCategory = category;
      productsExpanded = false;

      tabs.forEach((tab) => {
        const active = tab.dataset.categoryTab === category;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      applyProductVisibility();
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => filterProducts(tab.dataset.categoryTab), { signal });
    });

    productsMore?.addEventListener('click', () => {
      productsExpanded = !productsExpanded;
      applyProductVisibility();
    }, { signal });

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => applyProductVisibility(), 120);
    }, { signal });

    productCards.forEach((card) => {
      card.addEventListener('click', () => openModal(card), { signal });
    });

    modalCloseButtons.forEach((button) => {
      button.addEventListener('click', closeModal, { signal });
    });

    phoneInput.addEventListener('input', () => {
      if (selectedPhone && phoneInput.value !== selectedPhone.model) clearSelectedPhone({ keepInput: true });

      showSearchView();
      updateClearButton();
      window.clearTimeout(debounceTimer);
      window.clearTimeout(resizeTimer);
      searchController?.abort();
      phoneField.classList.remove('is-searching');
      hideResults();
      optionButtons = [];

      const query = phoneInput.value.trim();
      if (normalizeInput(query).length < MIN_QUERY_LENGTH) {
        setHelp(initialHelp);
        return;
      }

      setHelp('Searching phone models…');
      debounceTimer = window.setTimeout(() => searchPhones(query), DEBOUNCE_MS);
    }, { signal });

    phoneClear.addEventListener('click', () => {
      window.clearTimeout(debounceTimer);
      window.clearTimeout(resizeTimer);
      searchController?.abort();
      phoneField.classList.remove('is-searching');
      phoneInput.value = '';
      clearSelectedPhone({ keepInput: true });
      phoneOptions.replaceChildren();
      optionButtons = [];
      hideResults();
      setHelp(initialHelp);
      phoneInput.focus();
    }, { signal });

    changePhone.addEventListener('click', () => {
      clearSelectedPhone({ keepInput: true });
      setHelp(initialHelp);
      phoneInput.select();
      phoneInput.focus();
      const query = phoneInput.value.trim();
      if (normalizeInput(query).length >= MIN_QUERY_LENGTH) {
        searchPhones(query);
      }
    }, { signal });

    phoneInput.addEventListener('keydown', (event) => {
      if (resultsWrap.hidden) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        activateOption(activeOptionIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        activateOption(activeOptionIndex - 1);
      } else if (event.key === 'Enter' && activeOptionIndex >= 0) {
        event.preventDefault();
        optionButtons[activeOptionIndex]?.click();
      } else if (event.key === 'Escape') {
        hideResults();
      }
    }, { signal });

    modalDialog.addEventListener('click', (event) => {
      if (!phoneField.contains(event.target) && !resultsWrap.hidden) hideResults();
    }, { signal });

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = [...modalDialog.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]:not([hidden])')]
        .filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, { signal });

    checkAnother.addEventListener('click', () => {
      resetChecker();
      window.requestAnimationFrame(() => phoneInput.focus());
    }, { signal });

    resultContact.addEventListener('click', async (event) => {
      event.preventDefault();

      const fallbackUrl = resultContact.href || root.dataset.contactUrl || '/pages/contact-us';
      const label = resultContact.querySelector('span');
      const originalLabel = label?.textContent;
      const copied = await copyText(buildSupportMessage());
      const opened = await openShopifyInbox();

      if (!opened) {
        window.location.assign(fallbackUrl);
        return;
      }

      if (copied && label) {
        label.textContent = 'Copied — paste in chat';
        window.setTimeout(() => {
          if (label.isConnected) label.textContent = originalLabel;
        }, 4000);
      }
    }, { signal });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!selectedPhone || !selectedProduct || !apiBase) return;

      compatibilityController?.abort();
      compatibilityController = new AbortController();
      submit.classList.add('is-loading');
      submit.disabled = true;
      hideResults();

      try {
        const query = new URLSearchParams({ phone: selectedPhone.key, product: selectedProduct.key });
        const response = await fetch(`${apiBase}/api/compatibility?${query}`, {
          signal: compatibilityController.signal,
          credentials: 'omit',
          headers: { Accept: 'application/json' },
        });
        const payload = await response.json();

        if (response.status === 404 && payload.code === 'PHONE_NOT_FOUND') {
          showResult({
            status: 'not-found',
            icon: '?',
            kicker: 'MODEL NOT FOUND',
            title: "We couldn't find this phone.",
            message: 'This exact model is not in our compatibility database yet.',
            phone: selectedPhone.model,
            product: selectedProduct.name,
            statusLabel: 'Model not found',
            contact: true,
          });
          return;
        }

        if (!response.ok || payload.success !== true) {
          throw new Error(payload.message || 'Compatibility check failed.');
        }

        const status = payload.data.status;
        const phoneName = payload.data.phone.model;
        const productName = selectedProduct.name;
        const testedOs = payload.data.testedOsVersion || '';

        if (status === 'compatible') {
          showResult({
            status,
            icon: '✓',
            kicker: 'COMPATIBLE',
            title: "You're good to go.",
            message: `${phoneName} is compatible with ${productName}.`,
            phone: phoneName,
            product: productName,
            statusLabel: 'Compatible',
            os: testedOs,
            contact: false,
          });
        } else if (status === 'incompatible') {
          showResult({
            status,
            icon: '×',
            kicker: 'NOT COMPATIBLE',
            title: "This combination isn't supported.",
            message: `${phoneName} is not compatible with ${productName}.`,
            phone: phoneName,
            product: productName,
            statusLabel: 'Not Compatible',
            os: testedOs,
            contact: true,
          });
        } else {
          showResult({
            status: 'untested',
            icon: '?',
            kicker: 'NOT TESTED YET',
            title: "We haven't confirmed this one yet.",
            message: `We haven't confirmed compatibility between ${phoneName} and ${productName} yet.`,
            phone: phoneName,
            product: productName,
            statusLabel: "Haven't tested yet",
            contact: true,
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          showResult({
            status: 'error',
            icon: '!',
            kicker: 'CHECK UNAVAILABLE',
            title: 'Please try again.',
            message: 'The compatibility service is temporarily unavailable.',
            phone: selectedPhone?.model || '',
            product: selectedProduct?.name || '',
            statusLabel: 'Unavailable',
            contact: true,
          });
        }
      } finally {
        submit.classList.remove('is-loading');
        updateSubmit();
      }
    }, { signal });

    filterProducts(tabs.find((tab) => tab.classList.contains('is-active'))?.dataset.categoryTab || 'phone-thermal-camera');
    if (!apiBase) setHelp('Add the Compatibility API base URL in this section’s theme settings.');
    updateClearButton();
    updateSubmit();

    root._zlCompatibilityCleanup = () => {
      window.clearTimeout(debounceTimer);
      window.clearTimeout(resizeTimer);
      searchController?.abort();
      compatibilityController?.abort();
      root._zlCompatibilityController?.abort();
      modal.hidden = true;
      if (!document.querySelector('.zl-compatibility-modal:not([hidden])')) {
        document.body.classList.remove('zl-compatibility-modal-open');
      }
    };
  }

  function mount(root) {
    const controller = new AbortController();
    root._zlCompatibilityController = controller;
    root._zlCompatibilitySignal = controller.signal;
    initialize(root);
  }

  document.querySelectorAll(SELECTOR).forEach(mount);

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector?.(SELECTOR) || (event.target.matches?.(SELECTOR) ? event.target : null);
    if (root) mount(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.(SELECTOR) || (event.target.matches?.(SELECTOR) ? event.target : null);
    root?._zlCompatibilityCleanup?.();
  });
})();
